import { MatchLevelItem } from "@type/applicant";
import { AccessLevelOption } from "@type/auth";

export const employmentStatusOptions: Record<string, string> = {
  "employed-full-time": "Employed full-time",
  "employed-part-time": "Employed part-time",
  "notice-period": "Serving notice period",
  "unemployed": "Unemployed",
  "freelance": "Freelance / Contract",
  "student": "Student",
  "other": "Other",
};

export const getEmploymentStatusLabel = (value: string): string => {
  return employmentStatusOptions[value] || value;
};

export const getWorkAuthorizationOptions = (country: string) => [
  { value: "citizen", label: `Yes, I am a citizen of ${country}` },
  { value: "permanent_resident", label: `Yes, I am a permanent resident of ${country}` },
  { value: "work_visa", label: "Yes, I have a valid work visa or permit" },
  { value: "needs_visa", label: "No, I will need a work visa to work here" },
];

export const getWorkAuthorizationLabel = (value: string, country: string): string => {
  const option = getWorkAuthorizationOptions(country).find((o) => o.value === value);
  return option?.label || value;
};

export const availabilityOptions = [
  { value: "immediately", label: "Immediately" },
  { value: "within-2-weeks", label: "Within 2 weeks" },
  { value: "within-1-month", label: "Within 1 month" },
  { value: "1-2-months", label: "1–2 months" },
  { value: "more-than-2-months", label: "More than 2 months" },
];

export const defaultPagination = {
  page: 1,
  limit: 10,
};

export const defaultPaginationJob = {
  page: 1,
  limit: 12,
};

export const mapJobRateFrequency = {
  Hourly: "Hour",
  Weekly: "Week",
  Monthly: "Month",
  Yearly: "Year",
};

export const labelWorkOptionFulltime = "Full time";
export const listCommitmentTypes = ["Full time", "Contract", "Internship"];
export const listWorkArrangement = ["Onsite", "Hybrid", "Remote"];
export const listCurrency = ["USD", "SGD", "MYR", "IDR", "HKD"];
export const maxFileSize = 5 * 1024 * 1024;

export const statusApplicantOptions = [
  "Pending Review",
  "Interview Sent",
  "Interview Resent",
  "Offer Sent",
  // "Offer Accepted",
  "Offer Resent",
  "Offer Rejected",
  "Rejected",
  // "Application Reopened",
  "Hired",
  "Shortlisted"
];

export const statusSuggestionCandidationOptions = [
  "All Statuses",
  "Invited to Apply",
  "Not Invited",
  "Invitation Expired",
];

export const matchLevelApplicantOptions: MatchLevelItem[] = [
  {
    label: "All Match Levels",
    value: "all",
  },
  {
    label: "High Match",
    value: "High",
  },
  {
    label: "Medium Match",
    value: "Medium",
  },
  {
    label: "Low Match",
    value: "Low",
  },
];

export const interviewStatusNone = "None";
export const interviewStatusPendingInterview = "Pending Interview";
export const interviewStatusPendingVerification = "Pending Verification";
export const interviewStatusComplete = "Complete";
export const interviewStatusAborted = "Aborted";
export const interviewStatusPartial = "Partial";
export const interviewStatusInComplete = "Incomplete";
export const interviewStatusInterviewReady = "Interviewer Ready";
export const interviewStatusPendingApproval = "Pending Approval";
export const interviewStatusPendingProcessing = "Pending Processing";
export const interviewStatusPendingReview = "Pending Review";

export const contractStatusOfferRejected = "Offer Rejected";
export const payRateFrequencies = ["Weekly", "Monthly", "Yearly", "One Time"];

export const applicationStatusMap = {
  pendingReview: "Pending Review",
  interviewSent: "Interview Sent",
  interviewResent: "Interview Resent",
  offerSent: "Offer Sent",
  offerAccepted: "Offer Accepted",
  offerResent: "Offer Resent",
  offerRejected: "Offer Rejected",
  rejected: "Rejected",
  applicationReopened: "Application Reopened",
  hired: "Hired",
  // status for suggestion candidate
  notInvited: "Not Invited",
  invitedToApply: "Invited to Apply",
  invitationExpired: "Invitation Expired",
  invitedFUSent: "Invitation Expired - FU sent",
};

export const employerDashboardRoles = ["hr", "admin"];


export const stageApplyInfo = {
  resume: {
    title: "Resume Not Matched",
    message:
      "Unfortunately, your resume does not meet the requirements for this position.",
    skills: [
      "Technical expertise",
      "Relevant experience",
      "Required qualifications",
    ],

    recommendations: [
      "Update your resume with more specific technical skills",
      "Highlight relevant projects and experiences",
      "Consider gaining additional certifications",
    ],
  },
  interview: {
    title: "Interview Not Matched",
    message:
      "Unfortunately, your AI interview responses did not meet our requirements.",
    skills: [
      "Communication skills",
      "Problem-solving approach",
      "Technical knowledge",
    ],

    recommendations: [
      "Practice articulating your thoughts more clearly",
      "Focus on structured problem-solving techniques",
      "Review fundamental concepts in your field",
    ],
  },
  assessment: {
    title: "Screening Not Matched",
    message:
      "Unfortunately, your screening results did not meet our requirements.",
    skills: ["Technical implementation", "Code quality", "Problem analysis"],
    recommendations: [
      "Practice coding challenges regularly",
      "Focus on writing clean, maintainable code",
      "Work on breaking down complex problems",
    ],
  },
};

export const accessLevels: AccessLevelOption[] = [
  {
    value: "write",
    label: "Admin",
    description:
      "Has full access to manage workspace settings and all training campaigns.",
  },
  {
    value: "read",
    label: "Member",
    description: "Has view-only workspace access only. They are restricted from performing actions on the platform.",
  },
];

export const retakeReasons = [
  {
    value: "technical-issue",
    label: "Technical Issue",
    description: "Something went wrong, and I couldn't continue.",
  },
  {
    value: "interrupted",
    label: "Interrupted",
    description: "I had to leave early or was interrupted.",
  },
  {
    value: "just-testing",
    label: "Just Testing",
    description: "I was only testing the system.",
  },
  {
    value: "improve-performance",
    label: "Improve Performance",
    description: "I want to try again for a better outcome.",
  },
  {
    value: "other",
    label: "Other",
    description: "My reason isn't listed here.",
  },
];


export const defaultToneAndCommunication = [
  {
    id: "professional-clear-approachable",
    label: "Professional, clear, and approachable",
    description:
      "A balanced communication style that is easy to understand, respectful, and suitable for all interview types.",
  },
  {
    id: "analytical",
    label: "Analytical and detail oriented",
    description:
      "Ideal for tech, engineering, data, finance.",
  },
  {
    id: "customer-centric",
    label: "Customer centric and empathetic",
    description:
      "Useful for customer support, healthcare, service roles.",
  },
  {
    id: "persuasive",
    label: "Persuasive and confident",
    description:
      "Good for sales, marketing, business development.",
  },
  {
    id: "leadership",
    label: "Leadership focused and directive",
    description:
      "Useful for manager or senior leadership interviews.",
  },
  {
    id: "creative",
    label: "Creative and open minded",
    description:
      "Perfect for design, product, creative, research roles.",
  },
]

export const defaultInterviewFlow = [
  {
    id: "open-ended-natural-flow",
    label:
      "Ask open-ended questions and progress naturally between topics",
    description:
      "Ensures the AI encourages thoughtful answers and guides the interview in a natural, structured flow.",
  },
  {
    id: "real-world-scenarios",
    label:
      "Explore real world scenarios relevant to the role",
    description:
      "For operational, engineering, logistics, or IT roles.",
  },
  {
    id: "behavioral-storytelling",
    label:
      "Prioritize behavioural storytelling using STAR style answers",
    description:
      "Great for HR, customer support, management.",
  },
  {
    id: "technical-reasoning",
    label:
      "Focus on technical reasoning and problem solving steps",
    description:
      "Important for software engineering, tech, analytics.",
  },
  {
    id: "domain-knowledge",
    label:
      "Validate domain knowledge before exploring soft skills",
    description:
      "For specialized fields like legal, medical, cybersecurity.",
  },
  {
    id: "industry-best-practices",
    label:
      "Compare candidate decisions against industry best practices",
    description:
      "Useful for quality assurance, compliance, finance, risk roles.",
  },
]

export const mapToneAndCommunication: Record<string, string> = {
  "professional-clear-approachable":
    "Professional, clear, and approachable",
  analytical: "Analytical and detail oriented",
  "customer-centric":
    "Customer centric and empathetic",
  persuasive: "Persuasive and confident",
  leadership: "Leadership focused and directive",
  creative: "Creative and open minded",
};

export const mapInterviewFlow: Record<string, string> = {
  "open-ended-natural-flow":
    "Ask open-ended questions and progress naturally between topics",
  "real-world-scenarios":
    "Explore real world scenarios relevant to the role",
  "behavioral-storytelling":
    "Prioritize behavioural storytelling using STAR style answers",
  "technical-reasoning":
    "Focus on technical reasoning and problem solving steps",
  "domain-knowledge":
    "Validate domain knowledge before exploring soft skills",
  "industry-best-practices":
    "Compare candidate decisions against industry best practices",
}

export const mapApplicantStage: Record<string, string> = {
  "Uploads": "Uploads",
  "Applied": "Applied",
  "Assessment": "Screening In Progress",
  "Assessment in Progress": "Screening In Progress",
  "Assessment In Progress": "Screening In Progress",
  "Needs Review": "Ready for Review",
  "Offers": "Offers",
  "Closed": "Closed",
}